import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs';
import { TaskService, TaskItem } from '../../services/task.service';
import { TaskConfig, TaskViewSchema } from '../../models/task-config.model';
import { FileTransferComponent } from '../file-transfer/file-transfer.component';
import { ToastService } from '../../services/toast.service';
import { ViewSchemaEditorComponent } from '../view-schema-editor/view-schema-editor.component';
import { buildIndexAppViewSchema, parseIndexAppViewSchema, safeParseJsonObject } from '../../utils/view-schema.utils';

@Component({
  selector: 'app-task-config',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FileTransferComponent, ViewSchemaEditorComponent],
  templateUrl: './task-config.component.html',
  styleUrl: './task-config.component.scss',
})
export class TaskConfigComponent implements OnInit {
  private fb = inject(FormBuilder);
  private taskService = inject(TaskService);
  private http = inject(HttpClient);
  private toastService = inject(ToastService);

  protected loading = signal(false);
  protected saving = signal(false);
  protected analyzing = signal(false);
  /** JSON キー解析で得た Formula 補完用フィールドキー */
  protected readonly formulaFieldKeySuggestions = signal<string[]>([]);

  /** 他コンバータ画面と同一のバケットコード */
  private readonly BUCKET_CODE = 'x93jkd8f-qpz1-mv8w';

  protected isExecuting = signal(false);
  protected hasExecuted = signal(false);
  protected isLoadingResult = signal(false);
  protected executeError = signal<string | null>(null);
  protected resultRows = signal<TaskItem[]>([]);
  protected viewMode = signal<'table' | 'raw'>('raw');

  configForm: FormGroup = this.fb.group({
    wbsTaskId: ['convert-demo-json-to-json', Validators.required],
    id: ['', Validators.required],
    name: ['', Validators.required],
    searchPath: ['', Validators.required],
    baseObjectPath: [''],
    viewSchemaJson: [''],
  });

  ngOnInit(): void {
    this.fetchConfig();
  }

  fetchConfig(): void {
    const taskId = this.configForm.get('wbsTaskId')?.value;
    if (!taskId) return;

    this.loading.set(true);

    this.taskService.getTaskFile(taskId).subscribe({
      next: (res) => {
        if (!res || !res.data) {
          this.loading.set(false);
          return;
        }
        const config = res.data;

        const converter = config.profiles?.actions?.converter;
        if (converter) {
          this.configForm.patchValue({
            wbsTaskId: taskId,
            id: config.id,
            name: config.name,
            searchPath: converter.target.searchPath,
            baseObjectPath: converter.target.baseObjectPath,
            viewSchemaJson:
              converter.viewSchema && typeof converter.viewSchema === 'object'
                ? JSON.stringify(converter.viewSchema, null, 2)
                : '',
          });
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Failed to fetch config', err);
        this.loading.set(false);
        this.configForm.patchValue({
          viewSchemaJson: JSON.stringify(
            {
              fields: [
                { fieldName: 'アイディ', formula: 'id' },
                { fieldName: 'anime_title', formula: 'title' },
                { fieldName: 'source', value: 'Annict' },
              ],
            },
            null,
            2,
          ),
        });
      },
    });
  }

  analyzeJsonKeys(): void {
    const searchPath = this.configForm.get('searchPath')?.value;
    const baseObjectPath = this.configForm.get('baseObjectPath')?.value;

    if (!searchPath) {
      this.toastService.error('検索パスを入力してください');
      return;
    }

    this.analyzing.set(true);

    const isUrl = searchPath.startsWith('http');
    const observable = isUrl ? this.http.get(searchPath) : this.taskService.getFileContent(searchPath);

    observable.subscribe({
      next: (data: unknown) => {
        let target: unknown = data;

        if (baseObjectPath) {
          const parts = baseObjectPath.split('.');
          for (const part of parts) {
            if (target && typeof target === 'object' && target !== null && part in (target as object)) {
              target = (target as Record<string, unknown>)[part];
            } else {
              console.warn(`Path ${part} not found in JSON`);
              break;
            }
          }
        }

        let sample: Record<string, unknown> | null = null;
        if (Array.isArray(target)) {
          sample = target[0] as Record<string, unknown>;
        } else if (typeof target === 'object' && target !== null) {
          sample = target as Record<string, unknown>;
        }

        if (sample) {
          const keys = Object.keys(sample);
          this.formulaFieldKeySuggestions.set([...new Set(keys)].sort((a, b) => a.localeCompare(b)));
          try {
            const currentSchema = parseIndexAppViewSchema(safeParseJsonObject(this.configForm.get('viewSchemaJson')?.value));
            const mergedFields = [...currentSchema.fields];

            keys.forEach((key) => {
              const exists = mergedFields.some((field) => field.formula === key || field.fieldName === key);
              if (!exists) {
                mergedFields.push({ fieldName: key, formula: key });
              }
            });

            const nextSchema = buildIndexAppViewSchema(
              {
                fields: mergedFields,
                where: currentSchema.where,
                filters: currentSchema.filters,
                groupBy: currentSchema.groupBy,
                orderBy: currentSchema.orderBy,
                skip: currentSchema.skip,
                take: currentSchema.take,
              },
              safeParseJsonObject(currentSchema.advancedJson),
            );
            this.configForm.patchValue({
              viewSchemaJson: nextSchema ? JSON.stringify(nextSchema, null, 2) : '',
            });
            this.toastService.success(`${keys.length} 個のキーを解析しました`);
          } catch {
            this.toastService.error('現在の View Schema を読み込めません。追加 JSON の形式を確認してください');
          }
        } else {
          this.toastService.error('有効なJSONデータが見つかりませんでした');
        }
        this.analyzing.set(false);
      },
      error: (err) => {
        console.error('Failed to analyze JSON', err);
        this.toastService.error('JSONの取得に失敗しました');
        this.analyzing.set(false);
      },
    });
  }

  onSubmit(): void {
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    const formValue = this.configForm.getRawValue();

    let viewSchema: TaskViewSchema | null = null;
    try {
      if (typeof formValue.viewSchemaJson === 'string' && formValue.viewSchemaJson.trim() !== '') {
        viewSchema = JSON.parse(formValue.viewSchemaJson) as TaskViewSchema;
      }
    } catch {
      this.saving.set(false);
      this.toastService.error('View Schema (JSON) の形式が正しくありません');
      return;
    }

    const config: TaskConfig = {
      id: formValue.id,
      name: formValue.name,
      wbsType: 'task',
      profiles: {
        actions: {
          actionType: 'execute',
          converter: {
            target: {
              searchPath: formValue.searchPath,
              baseObjectPath: formValue.baseObjectPath,
            },
            viewSchema: (viewSchema ?? { fields: [] }) as TaskConfig['profiles']['actions']['converter']['viewSchema'],
          },
        },
      },
    };

    this.taskService.postTaskFile(config).subscribe({
      next: () => {
        this.saving.set(false);
        this.toastService.success('設定を保存しました');
      },
      error: (err) => {
        this.saving.set(false);
        this.toastService.error('エラーが発生しました');
        console.error('Error:', err);
      },
    });
  }

  /**
   * タスクAPIのレスポンスはタスク種別により data が配列または { savePath } になるため両方に対応する。
   */
  executeTask(): void {
    const wbsTaskId = this.configForm.get('wbsTaskId')?.value || this.configForm.get('id')?.value;
    if (!wbsTaskId) {
      this.executeError.set('WBS Task ID またはタスクIDを入力してください');
      return;
    }

    this.isExecuting.set(true);
    this.executeError.set(null);
    this.hasExecuted.set(true);
    this.isLoadingResult.set(true);
    this.resultRows.set([]);

    this.taskService.runConvertTask(wbsTaskId, this.BUCKET_CODE).subscribe({
      next: (response: { resultCode: number; resultMessage?: string | null; data: unknown }) => {
        if (response.resultCode !== 0) {
          this.executeError.set(response.resultMessage || 'タスクの実行に失敗しました。');
          this.isExecuting.set(false);
          this.isLoadingResult.set(false);
          return;
        }

        const d = response.data;
        if (Array.isArray(d)) {
          this.resultRows.set(d as TaskItem[]);
          this.isExecuting.set(false);
          this.isLoadingResult.set(false);
          return;
        }

        if (d && typeof d === 'object' && d !== null && 'savePath' in d) {
          const savePath = (d as { savePath?: string }).savePath;
          if (savePath) {
            this.taskService
              .getFileContent(savePath)
              .pipe(finalize(() => this.isExecuting.set(false)))
              .subscribe({
                next: (rows) => {
                  this.resultRows.set(rows);
                  this.isLoadingResult.set(false);
                },
                error: (err) => {
                  console.error('File fetch error:', err);
                  this.executeError.set(`変換結果ファイル (${savePath}) の取得に失敗しました。`);
                  this.isLoadingResult.set(false);
                },
              });
            return;
          }
        }

        this.executeError.set('タスク結果の形式が認識できませんでした。');
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
      },
      error: (err) => {
        console.error('Task execution error:', err);
        this.executeError.set('タスクの実行に失敗しました。接続を確認してください。');
        this.isExecuting.set(false);
        this.isLoadingResult.set(false);
      },
    });
  }

  getColumns(): string[] {
    const rows = this.resultRows();
    if (rows.length === 0) return [];
    return Object.keys(rows[0] as object);
  }

  getRawJson(): string {
    return JSON.stringify(this.resultRows(), null, 2);
  }
}
